
import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "react-i18next";

interface DatePickerWithRangeProps {
  date: DateRange | undefined;
  setDate: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  className?: string;
}

export function DatePickerWithRange({
  date,
  setDate,
  className,
}: DatePickerWithRangeProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const [tempDateRange, setTempDateRange] = React.useState<DateRange | undefined>(date);
  const [selectingFrom, setSelectingFrom] = React.useState(true);

  // Reset temp date when popover opens to match current selected date
  React.useEffect(() => {
    if (isOpen) {
      setTempDateRange(date);
      setSelectingFrom(true);
    }
  }, [isOpen, date]);

  const handleSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) return;
    
    if (selectingFrom) {
      // First click - set the "from" date and await the "to" date
      setTempDateRange({ from: selectedDate, to: undefined });
      setSelectingFrom(false);
    } else {
      // Second click - complete the range
      const from = tempDateRange?.from;
      if (!from) return;
      
      // Compare dates to ensure correct order
      if (selectedDate < from) {
        setTempDateRange({ from: selectedDate, to: from });
      } else {
        setTempDateRange({ from, to: selectedDate });
      }
    }
  };
  
  const handleApply = () => {
    if (tempDateRange) {
      setDate({
        from: tempDateRange.from,
        to: tempDateRange.to || tempDateRange.from // If no "to" date, use "from" date
      });
    } else {
      setDate(undefined);
    }
    setIsOpen(false);
  };
  
  const handleCancel = () => {
    // Reset to previous selection
    setTempDateRange(date);
    setIsOpen(false);
  };

  const handleReset = () => {
    // Reset the selection process
    setSelectingFrom(true);
    setTempDateRange(undefined);
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>{t("date.selectRange")}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3">
            <div className="mb-2 text-sm font-medium">
              {selectingFrom 
                ? t("date.selectStartDate") 
                : t("date.selectEndDate")}
            </div>
            
            {/* Show temporary selection in the popover */}
            {tempDateRange?.from && (
              <div className="mb-4 text-sm">
                <span className="font-medium">{t("date.selected")}:</span>{" "}
                <span className="text-primary">{format(tempDateRange.from, "LLL dd, y")}</span>
                {tempDateRange.to && (
                  <>
                    {" - "}
                    <span className="text-primary">{format(tempDateRange.to, "LLL dd, y")}</span>
                  </>
                )}
              </div>
            )}

            <Calendar
              initialFocus
              mode="single"
              defaultMonth={tempDateRange?.from || new Date()}
              selected={selectingFrom ? tempDateRange?.from : tempDateRange?.to}
              onSelect={handleSelect}
              numberOfMonths={2}
              modifiers={{
                selected: tempDateRange?.from 
                  ? tempDateRange.to
                    ? [tempDateRange.from, tempDateRange.to]
                    : [tempDateRange.from]
                  : []
              }}
              modifiersStyles={{
                selected: { 
                  backgroundColor: 'var(--primary)', 
                  color: 'var(--primary-foreground)' 
                }
              }}
            />
            <div className="mt-4 flex justify-between space-x-2">
              <Button 
                variant="outline"
                size="sm"
                onClick={handleReset}
              >
                {t("common.reset")}
              </Button>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleCancel}
                >
                  {t("common.cancel")}
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleApply}
                  disabled={!tempDateRange?.from}
                >
                  {t("common.apply")}
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
